"use strict";
/* eslint-disable @typescript-eslint/no-explicit-any */
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCustomConstructor = exports.createStyled = void 0;
var core_1 = require("@emotion/core");
var styled_1 = __importDefault(require("@emotion/styled"));
var utility_1 = require("@taraai/utility");
var lodash_flow_1 = __importDefault(require("lodash.flow"));
var lodash_kebabcase_1 = __importDefault(require("lodash.kebabcase"));
var react_1 = __importDefault(require("react"));
var systemUiMap_1 = require("./systemUiMap");
function createStyled(_a) {
    var tokens = _a.tokens, breakpoints = _a.breakpoints, _b = _a.utils, utils = _b === void 0 ? {} : _b;
    // eslint-disable-next-line security/detect-unsafe-regex
    var tokenRegExp = /(?:([A-Za-z]+)\.)?(\$\d*[a-z][\dA-Za-z]+)/g;
    var breakpointLabels = new Set(__spreadArray(['initial'], (0, utility_1.keys)(breakpoints), true));
    function hasOnlyResponsiveKeys(responsiveKeys) {
        return responsiveKeys.every(function (responsiveKey) { return breakpointLabels.has(responsiveKey); });
    }
    function tokensToVars() {
        var rootStyles = {};
        Object.entries(tokens).forEach(function (_a) {
            var type = _a[0], defs = _a[1];
            Object.entries(defs).forEach(function (_a) {
                var name = _a[0], value = _a[1];
                rootStyles[tokenToVarName(type, name)] = value;
            });
        });
        Object.entries(tokens.colors).forEach(function (_a) {
            var colorName = _a[0], colorValue = _a[1];
            Object.entries(tokens.opacities).forEach(function (_a) {
                var opacityName = _a[0], opacityValue = _a[1];
                var hexOpacity = Math.round(opacityValue * 255)
                    .toString(16)
                    .toUpperCase()
                    .padStart(2, '0');
                rootStyles[tokenToVarName('fade', colorName, opacityName)] = "".concat(colorValue).concat(hexOpacity);
            });
        });
        return rootStyles;
    }
    function injectUtils(css) {
        var result = {};
        Object.entries(css).forEach(function (_a) {
            var key = _a[0], value = _a[1];
            var util = utils[key];
            if (util) {
                Object.assign(result, util(value));
            }
            else {
                result[key] = isObject(value) ? injectUtils(value) : value;
            }
        });
        return result;
    }
    function swapToken(name, type) {
        return typeof name === 'string'
            ? name.replace(tokenRegExp, function (matched, substituteType, part) {
                var _a;
                if (substituteType === void 0) { substituteType = type; }
                return substituteType && ((_a = tokens[substituteType]) === null || _a === void 0 ? void 0 : _a[part])
                    ? wrapVar(tokenToVarName(substituteType, part))
                    : matched;
            })
            : name;
    }
    function injectCustom(css) {
        return mapValues(css, function (value) {
            if (!isObject(value))
                return value;
            if (isCustomValue(value))
                return value.value;
            return injectCustom(value);
        });
    }
    function injectTokens(css) {
        return mapValues(css, function (value, key) {
            return isObject(value) ? injectTokens(value) : swapToken(value, systemUiMap_1.systemUiMap[key]);
        });
    }
    function swapBreakpoints(css) {
        var result = {};
        Object.entries(css).forEach(function (_a) {
            var key = _a[0], value = _a[1];
            if (!isObject(value)) {
                result[key] = value;
                return;
            }
            var entryKeys = (0, utility_1.keys)(value);
            if (!hasOnlyResponsiveKeys(entryKeys)) {
                result[key] = swapBreakpoints(value);
                return;
            }
            entryKeys.forEach(function (responsiveKey) {
                if (responsiveKey === 'initial') {
                    result[key] = value[responsiveKey];
                }
                else {
                    var query = breakpoints[responsiveKey];
                    if (!result[query]) {
                        result[query] = {};
                    }
                    result[query][key] = value[responsiveKey];
                }
            });
        });
        return result;
    }
    function injectVariants(variants, propsWithQueries) {
        return Object.entries(propsWithQueries).map(function (_a) {
            var _b;
            var key = _a[0], value = _a[1];
            var variant = variants[key];
            if (!isObject(value))
                return variant[String(value)];
            if (isCustomValue(value)) {
                if (!isCustomMapper(variant, value.customKey))
                    throw new Error('Tried using a custom value with a variant that does not support that (use `mapCustom`)');
                return variant[value.customKey](String(value.value));
            }
            return _b = {}, _b[key] = injectVariants(variants, value), _b;
        });
    }
    function getVariantPicker(variants) {
        var variantNames = new Set((0, utility_1.keys)(variants));
        return function (props) {
            return Object.fromEntries(Object.entries(props).filter(function (_a) {
                var key = _a[0];
                return variantNames.has(key);
            }));
        };
    }
    function validateTokens() {
        Object.entries(tokens).forEach(function (_a) {
            var type = _a[0], defs = _a[1];
            Object.entries(defs).forEach(function (_a) {
                var name = _a[0], value = _a[1];
                validateTokenName(name);
                if (type === 'colors') {
                    validateColor(value);
                }
            });
        });
    }
    function applyStylePipeline(css) {
        validatePropNames(css);
        return injectTokens(injectUtils(swapBreakpoints(injectCustom(css))));
    }
    validateTokens();
    return {
        fade: getCustomConstructor('customColor', function (color, opacity) { return wrapVar(tokenToVarName('fade', color, opacity)); }),
        GlobalStyles: function () {
            return react_1.default.createElement(core_1.Global, { styles: { ':root': tokensToVars() } });
        },
        mapCustom: function (customKey, map) {
            var _a;
            return (_a = {}, _a[customKey] = Object.assign(map, { customKey: customKey }), _a);
        },
        mapBreakpoints: function (map) { return mapValues(breakpoints, function (value, name) { return map(name); }); },
        mapTokens: function (type, map) {
            return mapValues(tokens[type], function (value, name) {
                return map(wrapVar(tokenToVarName(type, name)), name);
            });
        },
        styled: function (tagOrComponent, css, variants) {
            if (variants === void 0) { variants = {}; }
            var notPassedProps = new Set((0, utility_1.keys)(variants));
            if (typeof tagOrComponent === 'string') {
                notPassedProps.add('as');
            }
            var options = {
                shouldForwardProp: function (prop) { return !notPassedProps.has(prop); },
            };
            var cleanCss = applyStylePipeline(css);
            var pickVariants = getVariantPicker(variants);
            var cleanVariants = mapValues(variants, function (variant) {
                return mapValues(variant, function (variantCss, variantName) {
                    return variantName.startsWith('custom') ? variantCss : applyStylePipeline(variantCss);
                });
            });
            return (0, styled_1.default)(tagOrComponent, options)((function (props) { return [
                cleanCss,
                injectVariants(cleanVariants, swapBreakpoints(pickVariants(props))),
            ]; }));
        },
        tokens: mapValues(tokens, function (values, type) {
            return mapValues(values, function (value, name) { return wrapVar(tokenToVarName(type, name)); });
        }),
    };
}
exports.createStyled = createStyled;
function getCustomConstructor(
/** A unique key that is later passed to mapCustom */
customKey, constructor) {
    return (0, lodash_flow_1.default)(constructor, function (value) { return ({ customKey: customKey, value: value, toString: function () { return value; } }); });
}
exports.getCustomConstructor = getCustomConstructor;
function mapValues(object, map) {
    return Object.fromEntries(Object.entries(object).map(function (_a) {
        var key = _a[0], value = _a[1];
        return [key, map(value, key)];
    }));
}
function isObject(value) {
    // Object(value) returns value if it's already an object
    return value === Object(value);
}
function isCustomMapper(value, customKey) {
    return customKey in value;
}
function isCustomValue(value) {
    return 'customKey' in value;
}
function wrapVar(varName) {
    return "var(".concat(varName, ")");
}
function tokenToVarName() {
    var parts = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        parts[_i] = arguments[_i];
    }
    return "--core-".concat(parts.map(lodash_kebabcase_1.default).join('-'));
}
var validTokenName = /^\$((\d*[a-z][\dA-Za-z]+)|(\d+%))$/;
function validateTokenName(tokenName) {
    if (!validTokenName.test(tokenName)) {
        throw new Error("Invalid token name \"".concat(tokenName, "\"; only tokens of form $camelCase are accepted"));
    }
}
var validColor = /^#[\dA-F]{6}$/;
function validateColor(color) {
    if (!validColor.test(color)) {
        throw new Error("Invalid color \"".concat(color, "\"; only colors of form #C0FFEE, with 6 hex digits (uppercase), are accepted"));
    }
}
function validatePropNames(css) {
    Object.entries(css).forEach(function (_a) {
        var name = _a[0], value = _a[1];
        if (isObject(value)) {
            if (isCustomValue(value))
                return;
            validatePropNames(value);
        }
        else
            validatePropName(name);
    });
}
var validPropName = /^([a-z]+|Moz[A-Z]|Webkit[A-Z])[A-Za-z]*$/;
var validCustomPropName = /^--[a-z]+[A-Za-z]*$/;
function validatePropName(name) {
    if (name.startsWith('--')) {
        if (!validCustomPropName.test(name))
            throw new Error("Invalid custom property name \"".concat(name, "\". Use the --camelCase format."));
    }
    else if (!validPropName.test(name))
        throw new Error("Invalid property name \"".concat(name, "\". Use the camelCase format."));
}
